import _ from "lodash";
import { computed, ref } from "vue";
import { defineStore } from "pinia";
import api from "@/api";
import type { Agreement, UserDetail } from "@/api";
import type { Attribute, BaseAttribute } from "@/attributes";
import type { FormErrors } from "@/forms";
import {
    ACCESS_ATTRIBUTE,
    areAccessesEqual,
    parseAccessAttribute,
    type AccessGroup,
} from "@/access";
import {
    AGREEMENTS_ATTRIBUTE,
    areAgreementsEqual,
    findMismatches,
    parseAgreementsAttribute,
    setAccessGroup as coupleAccessGroup,
    setAgreement as coupleAgreement,
    setAgreementsInherit as coupleAgreementsInherit,
    type AccessAttributes,
} from "@/agreements";
import { useAttributeLogStore } from "@/stores/attributeLog";
import { useOverviewStore } from "@/stores/overview";
import { useSessionStore } from "@/stores/session";

// The access page: agreements and the legacy access groups, saved together
// because enabling an agreement also sets its linked group.
export const useAccessStore = defineStore("access", () => {
    const overview = useOverviewStore();

    // Tag of data used to avoid data races
    const user = ref<UserDetail | undefined>(undefined);

    const state = ref<"editing" | "saving">("editing");
    const errors = ref<FormErrors | undefined>(undefined);

    const agreements = ref<Attribute<number[]> | undefined>(undefined);
    const access = ref<BaseAttribute<AccessGroup> | undefined>(undefined);
    const original = ref<AccessAttributes | undefined>(undefined);

    // Every agreement there is, the same for every user, so it survives a
    // user switch.
    const catalogue = ref<Agreement[]>([]);
    const catalogueState = ref<"idle" | "loading" | "loaded" | "failed">(
        "idle"
    );

    const attributes = computed((): AccessAttributes | undefined =>
        agreements.value && access.value
            ? { agreements: agreements.value, access: access.value }
            : undefined
    );

    const isAgreementsModified = computed(
        () =>
            !!agreements.value &&
            !!original.value &&
            !areAgreementsEqual(agreements.value, original.value.agreements)
    );

    const isAccessModified = computed(
        () =>
            !!access.value &&
            !!original.value &&
            !areAccessesEqual(access.value, original.value.access)
    );

    const hasBeenModified = computed(
        () => isAgreementsModified.value || isAccessModified.value
    );

    const canBeSet = computed(
        () => !!agreements.value?.canBeSet() || !!access.value?.canBeSet()
    );

    const mismatches = computed(() =>
        attributes.value && catalogueState.value === "loaded"
            ? findMismatches(attributes.value, catalogue.value)
            : []
    );

    const updateUser = (updatedUser: UserDetail) => {
        user.value = updatedUser;

        agreements.value = parseAgreementsAttribute(
            updatedUser.attributes[AGREEMENTS_ATTRIBUTE]
        );
        access.value = parseAccessAttribute(
            updatedUser.attributes[ACCESS_ATTRIBUTE]
        );
        original.value = {
            agreements: _.cloneDeep(agreements.value),
            access: _.cloneDeep(access.value),
        };

        state.value = "editing";
        errors.value = undefined;
    };

    const fetchCatalogue = async () => {
        if (
            catalogueState.value === "loading" ||
            catalogueState.value === "loaded"
        ) {
            return;
        }

        catalogueState.value = "loading";

        try {
            catalogue.value = await api.agreements.list();
            catalogueState.value = "loaded";
        } catch (error) {
            console.error("Error fetching agreements:", error);
            catalogueState.value = "failed";
        }
    };

    const setAgreement = (idAgreement: number, enabled: boolean) => {
        if (attributes.value) {
            coupleAgreement(
                attributes.value,
                catalogue.value,
                idAgreement,
                enabled
            );
        }
    };

    const setAccessGroup = (bit: bigint, enabled: boolean) => {
        if (attributes.value) {
            coupleAccessGroup(attributes.value, catalogue.value, bit, enabled);
        }
    };

    const setAgreementsInherit = (inherit: boolean) => {
        if (attributes.value && original.value) {
            coupleAgreementsInherit(attributes.value, original.value, inherit);
        }
    };

    const setAccessInherit = (inherit: boolean) => {
        if (access.value?.canBeSet()) {
            access.value.inherit = inherit;
        }
    };

    const updateAgreementsWhocanset = (whocanset: number) => {
        if (agreements.value) {
            agreements.value.whocanset = whocanset;
        }
    };

    const updateAccessWhocanset = (whocanset: number) => {
        if (access.value) {
            access.value.whocanset = whocanset;
        }
    };

    const addSite = (idSite: number) => {
        if (access.value && !access.value.value.sites.includes(idSite)) {
            access.value.value.sites.unshift(idSite);
        }
    };

    const removeSite = (idSite: number) => {
        if (access.value) {
            access.value.value.sites = access.value.value.sites.filter(
                (id) => id !== idSite
            );
        }
    };

    const setSources = (sources: string) => {
        if (access.value) {
            access.value.value.sources = sources;
        }
    };

    const reset = () => {
        if (original.value) {
            agreements.value = _.cloneDeep(original.value.agreements);
            access.value = _.cloneDeep(original.value.access);
        }
        errors.value = undefined;
    };

    const save = async () => {
        const session = useSessionStore();
        if (!session.canSave) {
            errors.value = {
                general: "You do not have permission to save changes.",
            };
            return;
        }

        if (!user.value || !agreements.value || !access.value) {
            return;
        }

        const idUser = user.value.id_user;

        // Sanity check that stores has not gotten out of sync
        if (overview.idUser !== idUser) {
            errors.value = {
                general: "Users out of sync. Please refresh.",
            };
            return;
        }

        const changed: Record<string, BaseAttribute<unknown>> = {};
        if (isAgreementsModified.value) {
            changed[AGREEMENTS_ATTRIBUTE] = agreements.value;
        }
        if (isAccessModified.value) {
            changed[ACCESS_ATTRIBUTE] = access.value;
        }

        if (_.isEmpty(changed)) {
            return;
        }

        state.value = "saving";
        errors.value = undefined;

        try {
            const updatedUser = await api.users.patchAttributes(
                idUser,
                changed
            );

            useAttributeLogStore().invalidate(Object.keys(changed));

            if (user.value.id_user === idUser) {
                updateUser(updatedUser);
            }
        } catch {
            errors.value = { general: "Error when saving" };
        }

        if (user.value.id_user === idUser) {
            state.value = "editing";
        }
    };

    return {
        user,
        state,
        errors,
        agreements,
        access,
        original,
        catalogue,
        catalogueState,
        hasBeenModified,
        canBeSet,
        mismatches,
        updateUser,
        fetchCatalogue,
        setAgreement,
        setAccessGroup,
        setAgreementsInherit,
        setAccessInherit,
        updateAgreementsWhocanset,
        updateAccessWhocanset,
        addSite,
        removeSite,
        setSources,
        reset,
        save,
    };
});
